import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import revenueApi from '../../../utils/api/revenue';

interface AddAccountRevenueShareProperties {
  closeModal: (successMessage?: string) => void;
  revenueType?: 'account' | 'station';
  open?: boolean;
}

interface RevenueShareFormState {
  accountCustomId: string;
  stationId: string;
  partnerRevenueSharePercentage: number | null;
  cpoRevenueSharePercentage: number | null;
}

const defaultFormState: RevenueShareFormState = {
  accountCustomId: '',
  stationId: '',
  partnerRevenueSharePercentage: null,
  cpoRevenueSharePercentage: null,
};

/**
 * Add Revenue Share side panel form component, shared by both the account
 * and station revenue management tabs. Lets the user pick a partner account
 * (and, in station mode, one of its stations) and set the partner/CPO
 * revenue share split - the two percentage fields are kept balanced to
 * always sum to 100.
 * @param {AddAccountRevenueShareProperties} properties - The properties
 * @param {(successMessage?: string) => void} properties.closeModal - Closes the side panel, optionally
 *   passing a success message so the parent can show a success modal and refresh
 * @param {'account' | 'station'} [properties.revenueType] - Whether this panel is adding an
 *   account-level or station-level revenue share; station mode shows an extra station dropdown
 * @param {boolean} [properties.open] - Whether the side panel is currently open; the account
 *   list is only fetched while open so the API isn't called while the panel is hidden
 * @returns {any} The rendered component
 */
function AddAccountRevenueShare({
  closeModal,
  revenueType = 'account',
  open = false,
}: AddAccountRevenueShareProperties) {
  const { t } = useTranslation('revenue');
  const isStationMode = revenueType === 'station';
  const [accountOptions, setAccountOptions] = useState<{ label: string; value: string }[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [stationOptions, setStationOptions] = useState<{ label: string; value: string }[]>([]);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [formState, setFormState] = useState<RevenueShareFormState>(defaultFormState);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Fetches the partner account list for the account select field
   * @returns {Promise<void>} Resolves once the account list is loaded
   */
  const fetchAccountOptions = async () => {
    setAccountsLoading(true);
    try {
      const response = await revenueApi.fetchAccountList();
      setAccountOptions(
        (response?.data?.results || response?.data || []).map((account: any) => ({
          label: account?.accountName,
          value: account?.accountCustomId,
        })),
      );
    } catch (error) {
      setAccountOptions([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  /**
   * Fetches the charging stations belonging to the selected account for the
   * station select field
   * @param {string} accountCustomId - The selected account's custom id
   * @returns {Promise<void>} Resolves once the station list is loaded
   */
  const fetchStationOptions = async (accountCustomId: string) => {
    setStationsLoading(true);
    try {
      const response = await revenueApi.fetchChargingStationsList(accountCustomId);
      setStationOptions(
        (response?.data?.results || response?.data || []).map((station: any) => ({
          label: station?.name,
          value: station?.id || station?._id,
        })),
      );
    } catch (error) {
      setStationOptions([]);
    } finally {
      setStationsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAccountOptions();
    }
  }, [open]);

  /**
   * Function to update the selected account. In station mode, switching the
   * account clears the previously selected station and reloads the station
   * options for the newly selected account.
   * @param {string} name - The field name
   * @param {string | number} value - The selected account custom id
   */
  const handleAccountChange = (name: string, value: string | number) => {
    const accountCustomId = String(value);
    setFormState((previous) => ({ ...previous, accountCustomId, stationId: '' }));

    if (isStationMode) {
      setStationOptions([]);
      if (accountCustomId) {
        fetchStationOptions(accountCustomId);
      }
    }
  };

  /**
   * Function to update the selected station
   * @param {string} name - The field name
   * @param {string | number} value - The selected station id
   */
  const handleStationChange = (name: string, value: string | number) => {
    setFormState((previous) => ({ ...previous, stationId: String(value) }));
  };

  /**
   * Function to update a revenue share percentage field. Partner and CPO
   * shares are kept balanced to always sum to 100 - editing one field
   * auto-fills the other with the remainder.
   * @param {string} name - The field name (partnerRevenueSharePercentage or cpoRevenueSharePercentage)
   * @param {string | number} value - The new field value
   */
  const handlePercentageChange = (name: string, value: string | number) => {
    const numericValue = value === '' ? null : Number(value);
    const counterpartName =
      name === 'partnerRevenueSharePercentage'
        ? 'cpoRevenueSharePercentage'
        : 'partnerRevenueSharePercentage';
    const counterpartValue =
      numericValue === null ? null : Math.round((100 - numericValue) * 100) / 100;

    setFormState((previous) => ({
      ...previous,
      [name]: numericValue,
      [counterpartName]: counterpartValue,
    }));
  };

  const isFormComplete =
    !!formState.accountCustomId &&
    (!isStationMode || !!formState.stationId) &&
    formState.partnerRevenueSharePercentage !== null &&
    formState.cpoRevenueSharePercentage !== null;

  /**
   * Function to submit the new revenue share. Used for both account-level and
   * station-level submissions - the payload only carries a stationId in station mode.
   * @returns {Promise<void>} Resolves once the save request completes
   */
  const handleSubmit = async () => {
    setShowFormErrors(true);
    if (!isFormComplete) return;

    setSubmitting(true);
    try {
      await revenueApi.addAccountRevenueShare({
        accountCustomId: formState.accountCustomId,
        ...(isStationMode ? { stationId: formState.stationId } : {}),
        partnerRevenueSharePercentage: Number(formState.partnerRevenueSharePercentage),
        cpoRevenueSharePercentage: Number(formState.cpoRevenueSharePercentage),
      });
      closeModal('Revenue share added successfully');
    } catch (error) {
      setSubmitting(false);
    }
  };

  return (
    <div className="add-account-revenue-share">
      <p className="add-account-revenue-share__title">
        {t('addRevenueShare', 'Add Revenue Share')}
      </p>

      <div className="add-account-revenue-share__form">
        <SelectFiled
          name="accountCustomId"
          inputLabel={t('accountName', 'Account Name')}
          placeholder={t('selectAccountName', 'Select Account Name')}
          options={accountOptions}
          loading={accountsLoading}
          selectedValue={formState.accountCustomId}
          onChangeValue={handleAccountChange}
          error={showFormErrors && !formState.accountCustomId}
          errorMessage={t('accountNameRequired', 'Account Name Required')}
          valueRequired
          enableSearch
        />

        {isStationMode && (
          <SelectFiled
            name="stationId"
            inputLabel={t('stationName', 'Station Name')}
            placeholder={t('selectStationName', 'Select Station Name')}
            options={stationOptions}
            loading={stationsLoading}
            selectedValue={formState.stationId}
            onChangeValue={handleStationChange}
            error={showFormErrors && !formState.stationId}
            errorMessage={t('stationNameRequired', 'Station Name Required')}
            valueRequired
            enableSearch
            disabled={!formState.accountCustomId}
          />
        )}

        <CustomInput
          inputLabel={t('partnerRevenueShare', 'Partner Revenue Share %')}
          name="partnerRevenueSharePercentage"
          type="number"
          value={formState.partnerRevenueSharePercentage}
          onChangeValue={handlePercentageChange}
          placeholder=""
          maxValue={100}
          decimalLimit={2}
          valueRequired
          error={showFormErrors && formState.partnerRevenueSharePercentage === null}
          errorMessage={t('partnerRevenueShareRequired', 'Partner Revenue Share Required')}
        />

        <CustomInput
          inputLabel={t('cpoRevenueShare', 'CPO Revenue Share %')}
          name="cpoRevenueSharePercentage"
          type="number"
          value={formState.cpoRevenueSharePercentage}
          onChangeValue={handlePercentageChange}
          placeholder=""
          maxValue={100}
          decimalLimit={2}
          valueRequired
          error={showFormErrors && formState.cpoRevenueSharePercentage === null}
          errorMessage={t('cpoRevenueShareRequired', 'CPO Revenue Share Required')}
        />

        <div className="add-account-revenue-share__footer">
          <MyButton
            label={t('cancel')}
            buttonType="error"
            onClickFunc={() => closeModal()}
            disableButton={submitting}
          />
          <MyButton
            label={t('submit')}
            buttonType="submit"
            onClickFunc={handleSubmit}
            disableButton={submitting}
            loading={submitting}
          />
        </div>
      </div>
    </div>
  );
}

AddAccountRevenueShare.defaultProps = {
  revenueType: 'account',
  open: false,
};

export default AddAccountRevenueShare;
